// Utility type to convert camelCase to snake_case
type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}`
  : S;

// Mapped type to convert all keys in an interface from camelCase to snake_case
export type SnakeCase<T> = {
  [K in keyof T as CamelToSnakeCase<string & K>]: T[K] extends (infer U)[]
    ? SnakeCase<U>[]
    : T[K] extends object
      ? SnakeCase<T[K]>
      : T[K];
};

// Convert snake_case object to camelCase
function toCamelCaseKey(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function mapSnakeToCamel<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => mapSnakeToCamel(item)) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = toCamelCaseKey(key);
        result[camelKey] = mapSnakeToCamel((obj as Record<string, unknown>)[key]);
      }
    }
    return result as T;
  }

  return obj as T;
}
